<template>
  <component
    :is="as"
    :class="['btn', kind, { block, large, floating, loading }]"
    :type="type"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <span v-if="loading">
      <spinner
        :class="['loading-spinner', { 'has-text': loadingText }]"
        size="16"
        inline
        :light="kind !== 'secondary'"
      />

      <span v-if="loadingText" class="text">{{ loadingText }}</span>
    </span>

    <span v-else>
      <span><slot /></span>
      <icon v-if="icon" :name="icon" class="icon" />
    </span>
  </component>
</template>

<script>
import Icon from './Icon';
import Spinner from './Spinner';

export default {
  components: {
    Icon,
    Spinner,
  },

  props: {
    kind: {
      type: String,
      options: ['primary', 'secondary', 'error', 'google', 'apple', 'ebay', 'flat', 'inverted'],
      default: 'primary',
    },
    as: {
      type: String,
      default: 'button',
    },
    type: {
      type: String,
      default: 'button',
    },
    block: Boolean,
    loading: Boolean,
    loadingText: {
      type: String,
      default: 'Saving',
    },
    icon: String,
    large: Boolean,
    floating: Boolean,
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.btn {
  @include transition(box-shadow);
  flex-shrink: 0;
  max-width: 100%;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  border: none;
  border-radius: $border-radius;
  height: 48px;
  padding: 0 $gp;
  font-family: $font-family;
  font-weight: bold;
  font-size: 16px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  outline: none;
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  background-color: var(--button-background-color);
  color: var(--button-text-color);
  text-decoration: none !important;
  --button-background-color: #{$color-green-1};
  --button-text-color: #{$color-white};
  --button-hover-color: #{rgba($color-black, 0.3)};
  --button-active-color: #{rgba($color-black, 0.5)};

  > span {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  &.secondary {
    --button-background-color: var(--theme-secondary-button);
    --button-text-color: var(--theme-secondary-button-color);
  }

  &.error {
    --button-background-color: #{$color-red-1};
    --button-text-color: #{$color-white};
  }

  &.flat {
    --button-background-color: transparent;
    --button-text-color: var(--theme-secondary-button-color);
  }

  &.google {
    --button-background-color: #{$color-blue-1};
    --button-text-color: #{$color-white};
  }

  &.apple {
    --button-background-color: #{$color-black};
    --button-text-color: #{$color-white};
  }

  &.ebay {
    border: solid 1px $color-green-1;
    --button-background-color: var(--theme-background-color);
    --button-text-color: var(--theme-text-color);
  }

  &.inverted {
    --button-background-color: #{$color-white};
    --button-text-color: #{$color-green-1};
  }

  @media($medium-up) {
    &.large {
      height: 60px;
      font-size: 24px;
      padding: 0 $gp * 1.5;
    }
  }

  &.floating {
    box-shadow: $elevation-1;

    &:hover {
      box-shadow: $elevation-1;
    }
  }

  &.loading {
    pointer-events: none;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 110%;
    height: 100%;
    background-color: var(--theme-button-hover-color);
    @include transition(all);
    transform: translateX(-150%);
  }

  &:hover:not([disabled]),
  &:focus:not([disabled]) {
    &:before {
      transform: translateX(-50%) skewX(-10deg);
    }
  }

  &:active:not([disabled]) {
    &:before {
      background-color: var(--theme-button-active-color);
    }
  }

  &:disabled {
    opacity: 0.5;
  }

  &.block {
    width: 100%;
  }
}

.loading-spinner.has-text {
  margin-right: $gp * .25;
}

.icon {
  font-size: 18px;
  margin-left: $gp * .5;
}
</style>
